import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';

import CloseIcon from '@mui/icons-material/CloseOutlined';
import PlayIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import SkipNextSharpIcon from '@mui/icons-material/SkipNextSharp';
import SkipPreviousSharpIcon from '@mui/icons-material/SkipPreviousSharp';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { toSecond } from './utilities';
import { normalizeTagData } from './common';
import gameService from '../../../services/game.service';
import PlayerTagEditRequestDialog from '../../player/PlayerTagEditRequestDialog';
import GameExportToEdits from '../games/tabs/overview/exportEdits';
import { TEAM_ICON_DEFAULT } from '../../../common/staticData';

import '../../player/Profile.css';

/**
 * Shared Video Player component used across all pages
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog closes, receives (hasUpdates: boolean)
 * @param {string|Array} video_url - Single video URL string OR array of { id, video_url } objects
 * @param {Array} tagList - List of tags/clips to display
 * @param {function} t - Translation function
 * @param {boolean} showEditDialog - Whether to show tag edit dialog (default: true)
 */
const SharedVideoPlayer = ({ open, onClose, video_url, tagList, t, showEditDialog = true }) => {
    const player = useRef(null);
    const [playRate, setPlayRate] = useState(1);
    const [play, setPlay] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [videoURL, setVideoURL] = useState('');
    const [videoList, setVideoList] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [tagEditOpen, setTagEditOpen] = useState(false);
    const [updated, setUpdated] = useState(0);
    const [videoReady, setVideoReady] = useState(false);
    const [pendingSeek, setPendingSeek] = useState(null);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [checkedClips, setCheckedClips] = useState({});
    const [exportOpen, setExportOpen] = useState(false);
    const [showTimeScore, setShowTimeScore] = useState(true);

    // Check if video_url is an array (multi-video mode) or string (single-video mode)
    const isMultiVideo = Array.isArray(video_url);

    const fastVideo = (param) => {
        player.current && player.current.seekTo(currentTime + param);
    };

    const onProgress = (seconds) => {
        if (tagList.length === 0) return;

        setCurrentTime(seconds);

        const start = toSecond(tagList[currentIndex].start_time);
        const end = toSecond(tagList[currentIndex].end_time);

        if (seconds >= end) {
            if (currentIndex < tagList.length - 1) PlayVideo(1);
            else {
                setPlay(false);
            }
        } else if (seconds <= start) {
            if (playRate !== 1) {
                setPlay(false);
                setPlayRate(1);
            }

            player.current.seekTo(start);
            setPlay(true);
        }
    };

    const PlayVideo = async (add) => {
        let index = currentIndex + add;

        if (index >= tagList.length) {
            setPlay(false);
            return;
        } else if (index < 0) return;

        setCurrentIndex(index);

        if (isMultiVideo) {
            const matchedVideo = videoList.filter((item) => Number(item.id) === Number(tagList[index].game_id))[0];
            if (matchedVideo) {
                const isSameVideo = matchedVideo.url === videoURL;
                if (isSameVideo) {
                    player.current.seekTo(toSecond(tagList[index].start_time));
                } else {
                    setPlay(false);
                    setVideoReady(false);
                    setVideoURL(matchedVideo.url);
                    setPendingSeek(toSecond(tagList[index].start_time));
                }
            }
        } else {
            player.current.seekTo(toSecond(tagList[index].start_time));
        }
    };

    const handleClipClick = (index) => {
        setCurrentIndex(index);

        if (isMultiVideo) {
            const matchedVideo = videoList.filter((item) => Number(item.id) === Number(tagList[index].game_id))[0];
            if (matchedVideo) {
                const isSameVideo = matchedVideo.url === videoURL;
                if (isSameVideo) {
                    player.current.seekTo(toSecond(tagList[index].start_time));
                    setPlay(true);
                } else {
                    setPlay(false);
                    setVideoReady(false);
                    setVideoURL(matchedVideo.url);
                    setPendingSeek(toSecond(tagList[index].start_time));
                }
            }
        } else {
            player.current.seekTo(toSecond(tagList[index].start_time));
            setPlay(true);
        }
    };

    const handleFullscreen = () => {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        }
    };

    const handleVideoReady = () => {
        if (pendingSeek !== null && player.current) {
            player.current.seekTo(pendingSeek);
            setPendingSeek(null);

            if (isFirstLoad) {
                setTimeout(() => {
                    setVideoReady(true);
                    setIsFirstLoad(false);
                    setPlay(true);
                }, 300);
            } else {
                setPlay(true);
            }
        }
    };

    // Handle multi-video mode: build video list
    useEffect(() => {
        if (isMultiVideo) {
            setVideoList([]);
            video_url.forEach((game) => {
                if (game.video_url.includes('youtube.com') || game.video_url.includes('youtu.be')) {
                    gameService.getNewStreamURL(game.video_url).then((res) => {
                        setVideoList((old) => [...old, { id: game.id, url: res.result.video_url }]);
                    });
                } else if (game.video_url.toLowerCase() !== 'no video') {
                    setVideoList((old) => [...old, { id: game.id, url: game.video_url }]);
                }
            });
        }
    }, [video_url, isMultiVideo]);

    // Handle single-video mode: set video URL directly
    useEffect(() => {
        if (!isMultiVideo && video_url) {
            if (video_url.includes('youtube.com') || video_url.includes('youtu.be')) {
                gameService.getNewStreamURL(video_url).then((res) => {
                    setVideoURL(res.url || res.result?.video_url || '');
                });
            } else if (video_url.toLowerCase() !== 'no video') {
                setVideoURL(video_url);
            }
        }
    }, [video_url, isMultiVideo]);

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setCurrentIndex(0);
            setVideoReady(false);
            setPlay(false);
            if (isMultiVideo) {
                setVideoURL('');
            }
            setPendingSeek(null);
            setIsFirstLoad(true);
            setCheckedClips({});
        }
    }, [open, isMultiVideo]);

    // Handle initial video load when videoList becomes available (multi-video mode)
    useEffect(() => {
        if (isMultiVideo && open && videoList.length > 0 && tagList.length > 0 && !videoURL) {
            const matchedVideo = videoList.filter((item) => Number(item.id) === Number(tagList[0].game_id))[0];
            if (matchedVideo) {
                setVideoURL(matchedVideo.url);
                setPendingSeek(toSecond(tagList[0].start_time));
            }
        }
    }, [open, videoList, tagList, videoURL, isMultiVideo]);

    // For single-video mode, seek to first clip when video is ready
    useEffect(() => {
        if (!isMultiVideo && open && videoURL && tagList.length > 0 && player.current) {
            setPendingSeek(toSecond(tagList[0].start_time));
        }
    }, [open, videoURL, tagList, isMultiVideo]);

    const getPeriodLabel = (item) => {
        if (item.period) {
            const period = item.period.toString();
            if (period === '1' || period === 'H1') return t('First Half');
            if (period === '2' || period === 'H2') return t('Second Half');
            return period;
        }
        return '';
    };

    const getClipDescription = (item) => {
        const playerName = item.player_name || item.player_names || item.name || item.full_name || '';
        const actionName = item.action_name || item.action_names || '';
        const actionType = item.action_type || item.action_type_name || item.action_type_names || '';
        const actionResult = item.action_result || item.action_result_name || item.action_result_names || '';

        const parts = [playerName, actionName, actionType, actionResult]
            .map(p => (p || '').toString().trim())
            .filter(p => p && p.length > 0);

        return parts.join(' - ') || 'Video clip';
    };

    // Checkbox handlers
    const handleCheckClip = (index) => (e) => {
        e.stopPropagation();
        setCheckedClips(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleSelectAll = () => {
        const allChecked = tagList.every((_, index) => checkedClips[index]);
        if (allChecked) {
            // Unselect all
            setCheckedClips({});
        } else {
            // Select all
            const newChecked = {};
            tagList.forEach((_, index) => {
                newChecked[index] = true;
            });
            setCheckedClips(newChecked);
        }
    };

    const getSelectedClips = () => {
        const selected = tagList.filter((_, index) => checkedClips[index]);
        return normalizeTagData(selected);
    };

    const selectedCount = Object.values(checkedClips).filter(Boolean).length;
    const allSelected = tagList.length > 0 && selectedCount === tagList.length;

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={() => onClose(updated > 0)}
            PaperProps={{
                style: { backgroundColor: '#1a1b1d' }
            }}
        >
            <DialogContent sx={{ p: 0, display: 'flex', height: '100vh', position: 'relative' }}>
                {/* Time/Score Overlay - fixed position over everything */}
                {showTimeScore && tagList.length > 0 && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 20,
                            left: '25%',
                            transform: 'translateX(-50%)',
                            zIndex: 99999,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            pointerEvents: 'none'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#17375E', px: 2, py: 0.5, borderRadius: '5px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.2rem', fontWeight: 500, color: '#ffffff' }}>
                                {`${tagList[currentIndex]?.period === 1 ? 'H1' : tagList[currentIndex]?.period === 2 ? 'H2' : 'OT'} - ${tagList[currentIndex]?.time_in_game || 0}'`}
                            </Typography>
                        </Box>
                        <img src={tagList[currentIndex]?.home_team_logo ? (tagList[currentIndex].home_team_logo.includes(process.env.REACT_APP_S3_URI) ? tagList[currentIndex].home_team_logo : process.env.REACT_APP_S3_URI + tagList[currentIndex].home_team_logo) : TEAM_ICON_DEFAULT} style={{ width: '40px', height: '40px', objectFit: 'contain', background: '#fff', borderRadius: '4px' }} alt="" />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', px: 2, py: 0.5, borderRadius: '5px', minWidth: '80px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1.2rem', fontWeight: 500, color: '#17375E' }}>
                                {`${tagList[currentIndex]?.home_team_goals ?? '-'} : ${tagList[currentIndex]?.away_team_goals ?? '-'}`}
                            </Typography>
                        </Box>
                        <img src={tagList[currentIndex]?.away_team_logo ? (tagList[currentIndex].away_team_logo.includes(process.env.REACT_APP_S3_URI) ? tagList[currentIndex].away_team_logo : process.env.REACT_APP_S3_URI + tagList[currentIndex].away_team_logo) : TEAM_ICON_DEFAULT} style={{ width: '40px', height: '40px', objectFit: 'contain', background: '#fff', borderRadius: '4px' }} alt="" />
                    </Box>
                )}
                <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
                    {/* Video Section - Left */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#000', position: 'relative' }}>
                        <Box sx={{ flex: 1, position: 'relative' }}>
                            {/* Loading overlay - only on first load for multi-video */}
                            {isMultiVideo && isFirstLoad && !videoReady && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: '#000',
                                        zIndex: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CircularProgress sx={{ color: '#fff' }} />
                                </Box>
                            )}
                            <ReactPlayer
                                url={videoURL}
                                ref={player}
                                onReady={handleVideoReady}
                                onPlay={() => setPlay(true)}
                                onPause={() => setPlay(false)}
                                onProgress={(p) => onProgress(p.playedSeconds)}
                                playing={play}
                                controls={false}
                                playbackRate={playRate}
                                width="100%"
                                height="100%"
                                style={{ position: 'absolute', top: 0, left: 0 }}
                            />
                        </Box>

                        {/* Video Controls */}
                        <Box sx={{ backgroundColor: '#1a1b1d', p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <IconButton onClick={() => PlayVideo(-1)} sx={{ color: '#fff' }}>
                                    <SkipPreviousSharpIcon />
                                </IconButton>
                                <IconButton onClick={() => fastVideo(-3)} sx={{ color: '#fff' }}>
                                    <FastRewindIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => {
                                        if (playRate === 1) setPlay((p) => !p);
                                        else setPlayRate(1);
                                    }}
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: '#333',
                                        '&:hover': { backgroundColor: '#444' },
                                        width: 48,
                                        height: 48
                                    }}
                                >
                                    {play && playRate === 1 ? <PauseIcon /> : <PlayIcon />}
                                </IconButton>
                                <IconButton onClick={() => fastVideo(3)} sx={{ color: '#fff' }}>
                                    <FastForwardIcon />
                                </IconButton>
                                <IconButton onClick={() => PlayVideo(1)} sx={{ color: '#fff' }}>
                                    <SkipNextSharpIcon />
                                </IconButton>
                                <FormControlLabel
                                    control={<Switch checked={showTimeScore} onChange={(e) => setShowTimeScore(e.target.checked)} size="small" />}
                                    label="T/S"
                                    sx={{ color: '#fff', ml: 2, '& .MuiFormControlLabel-label': { fontSize: '12px' } }}
                                />
                                <IconButton onClick={handleFullscreen} sx={{ color: '#fff' }}>
                                    <FullscreenIcon />
                                </IconButton>
                                {showEditDialog && tagList.length > 0 && (
                                    <Tooltip title={t('Request Correction')}>
                                        <IconButton
                                            onClick={() => {
                                                setPlay(false);
                                                setTagEditOpen(true);
                                            }}
                                            sx={{ color: '#fff' }}
                                        >
                                            <EditOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Clips List - Right */}
                    <Box sx={{ width: '400px', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
                        {/* Header with close button */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderBottom: '1px solid #e0e0e0' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px', pl: 1 }}>
                                {t('Clips')} ({tagList.length})
                            </Typography>
                            <IconButton onClick={() => onClose(updated > 0)} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Select All and Export controls */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    size="small"
                                    checked={allSelected}
                                    indeterminate={selectedCount > 0 && !allSelected}
                                    onChange={handleSelectAll}
                                    sx={{ p: 0.5 }}
                                />
                                <Typography sx={{ fontSize: '12px', color: '#6b7280', ml: 0.5 }}>
                                    {selectedCount > 0 ? `${selectedCount} ${t('selected')}` : t('Select All')}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                                disabled={selectedCount === 0}
                                onClick={() => setExportOpen(true)}
                                sx={{
                                    fontSize: '11px',
                                    textTransform: 'none',
                                    py: 0.25,
                                    px: 1,
                                    borderColor: '#e5e7eb',
                                    color: selectedCount > 0 ? '#f59e0b' : '#9ca3af',
                                    '&:hover': {
                                        borderColor: '#f59e0b',
                                        backgroundColor: '#fffbeb'
                                    }
                                }}
                            >
                                {t('Export to Edits')}
                            </Button>
                        </Box>

                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {tagList.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        p: 1.5,
                                        cursor: 'pointer',
                                        backgroundColor: currentIndex === index ? '#C5102C' : 'transparent',
                                        color: currentIndex === index ? '#fff' : '#1a1b1d',
                                        '&:hover': {
                                            backgroundColor: currentIndex === index ? '#C5102C' : '#f5f5f5'
                                        },
                                        borderBottom: '1px solid #e0e0e0'
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={!!checkedClips[index]}
                                        onChange={handleCheckClip(index)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            p: 0.5,
                                            mr: 1,
                                            color: currentIndex === index ? '#fff' : undefined,
                                            '&.Mui-checked': {
                                                color: currentIndex === index ? '#fff' : '#6366f1'
                                            }
                                        }}
                                    />
                                    <Box onClick={() => handleClipClick(index)} sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 600, minWidth: '20px', fontSize: '13px', color: 'inherit' }}>
                                                {index + 1}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'inherit' }}>
                                                {getPeriodLabel(item)}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '12px', opacity: 0.9, color: 'inherit', mt: 0.5 }}>
                                            {getClipDescription(item)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {showEditDialog && tagList.length > 0 && (
                    <PlayerTagEditRequestDialog
                        t={t}
                        open={tagEditOpen}
                        onClose={(flag) => {
                            setTagEditOpen(false);
                            setPlay(true);
                            if (flag) setUpdated((c) => c + 1);
                        }}
                        player={tagList[currentIndex]}
                        cur_player_id={tagList[currentIndex]?.player_id}
                    />
                )}

                {/* Export to My Edits Dialog */}
                <GameExportToEdits
                    t={t}
                    open={exportOpen}
                    onClose={() => setExportOpen(false)}
                    tagList={getSelectedClips()}
                    isTeams={true}
                />
            </DialogContent>
        </Dialog>
    );
};

export default SharedVideoPlayer;
